import { useCallback, useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, ScanText, FileSymlink, Copy, Download, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import TipTapEditor from "@/components/ui/shadcn-io/editor/TipTapEditor";
import { type PdfExtractionDocument } from "@/redux/api/documents.rtkq";
import { getDownloadURL, ref } from "firebase/storage";
import { storage, db } from "@/lib/firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useAuthContext } from "@/hooks/useAuthContext";
import type { HTMLContent } from "@tiptap/core";

interface PdfDocumentViewerProps {
  document: PdfExtractionDocument;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const PdfDocumentViewer = ({ document, isOpen, onOpenChange }: PdfDocumentViewerProps) => {
  const { user } = useAuthContext();
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Documents extracted before the pivot to the backend OCR pipeline hold HTML
  // and carry no `contentFormat`, so an absent value must read as "html".
  const format = document.contentFormat ?? "html";

  // Auto-save with debounce (2 seconds)
  const handleUpdate = useCallback(
    async (html: string) => {
      if (!user?.uid) return;

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set saving status
      setSaveStatus("saving");

      // Debounce for 2 seconds
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const docRef = doc(db, "users", user.uid, "documents", document.docId);
          await updateDoc(docRef, {
            extractedContent: html,
            updatedAt: serverTimestamp(),
          });
          setSaveStatus("saved");

          // Reset to idle after 2 seconds
          setTimeout(() => {
            setSaveStatus("idle");
          }, 2000);
        } catch (error) {
          console.error("Failed to save document:", error);
          setSaveStatus("error");
          toast.error("שגיאה בשמירת המסמך");
        }
      }, 2000);
    },
    [user?.uid, document.docId]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Copy to clipboard
  const handleCopyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(document.extractedContent);
      toast.success("התוכן הועתק ללוח");
    } catch (error) {
      toast.error("שגיאה בהעתקת התוכן");
    }
  }, [document.extractedContent]);

  // Download original PDF
  const handleDownloadOriginal = useCallback(async () => {
    try {
      const storageRef = ref(storage, document.originalPath);
      const url = await getDownloadURL(storageRef);

      // Create a temporary anchor element to trigger download
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.storageFileName;
      link.target = "_blank";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      toast.success("הקובץ ירד בהצלחה");
    } catch (error) {
      toast.error("שגיאה בהורדת הקובץ");
    }
  }, [document.originalPath, document.storageFileName]);

  const formatDate = (timestamp: any) => {
    try {
      return new Date(timestamp).toLocaleDateString("he-IL", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "תאריך לא זמין";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-none flex-col rounded-none p-0 sm:h-[95vh] sm:max-h-[95vh] sm:w-[95vw] sm:max-w-[95vw] sm:rounded-lg">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 mr-4">
              <div className="flex items-center gap-2">
                <DialogTitle className="text-xl font-semibold text-chen-900 text-right break-all">
                  {document.storageFileName}
                </DialogTitle>
                {/* Save Status Indicator */}
                {saveStatus === "saving" && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>שומר...</span>
                  </div>
                )}
                {saveStatus === "saved" && (
                  <div className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span>שמור</span>
                  </div>
                )}
                {saveStatus === "error" && (
                  <div className="flex items-center gap-1 text-sm text-red-600">
                    <span>שגיאה בשמירה</span>
                  </div>
                )}
              </div>
              <DialogDescription className="text-muted-foreground text-right mt-1">ערוך מסמך</DialogDescription>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Badge variant="secondary" className="bg-indigo-50 text-indigo-700">
              <FileText className="w-3 h-3 ml-1" />
              חילוץ PDF
            </Badge>
            <Badge
              variant={document.isScanned ? "default" : "secondary"}
              className={document.isScanned ? "bg-blue-50 text-blue-700" : "bg-gray-50 text-gray-700"}
            >
              <ScanText className="w-3 h-3 ml-1" />
              {document.isScanned ? "מסמך סרוק" : "מסמך טקסט"}
            </Badge>
            <Badge variant="secondary" className="bg-green-50 text-green-700">
              הושלם
            </Badge>
          </div>

          {/* Additional Info */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <FileSymlink className="w-4 h-4" />
              <span>תאריך: {formatDate(document.createdAt)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>גודל: {document.fileSize ? `${(document.fileSize / 1024).toFixed(2)} KB` : "לא זמין"}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>אורך: {document.extractedContent.length} תווים</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyToClipboard}
              className="border-slate-200 text-slate-800 hover:bg-slate-100"
            >
              <Copy className="w-4 h-4 ml-2" />
              העתק ללוח
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadOriginal}
              className="border-slate-200 text-slate-800 hover:bg-slate-100"
            >
              <Download className="w-4 h-4 ml-2" />
              הורד PDF מקורי
            </Button>
          </div>
        </DialogHeader>

        {/* Tiptap Editor - Read Only */}
        <div className="flex-1 overflow-hidden">
          <TipTapEditor
            key={`${document.docId}-${format}`}
            fileName={document.storageFileName}
            summaryHTML={document.extractedContent as HTMLContent}
            format={format}
            onUpdateCallback={handleUpdate}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PdfDocumentViewer;
